/**
 * Batch #14.176 — Inbox réception IMAP (DGFiscalFlow@dgcomptabilite.ca).
 *
 * Lecture seule. Liste à gauche, viewer à droite. Style Gmail simplifié.
 * Aucune fonction d'envoi.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Inbox, RefreshCw, Loader2, Mail, Paperclip,
  AlertCircle, CheckCircle2, Search, MailOpen, MailCheck,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


function formatRelativeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" });
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString("fr-CA", {
      day: "2-digit",
      month: "short",
      year: sameYear ? undefined : "numeric",
    });
  } catch {
    return iso;
  }
}

function MessageListItem({ msg, isSelected, onClick }) {
  const isUnread = !msg.is_read;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition relative ${
        isSelected ? "bg-blue-50 border-l-4 border-l-blue-500" : "hover:bg-slate-50 border-l-4 border-l-transparent"
      }`}
      data-testid={`inbox-msg-${msg.uid}`}
      data-unread={isUnread}
    >
      {isUnread && (
        <span
          className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500"
          aria-label="Non lu"
          data-testid={`inbox-unread-dot-${msg.uid}`}
        />
      )}
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className={`text-sm truncate flex-1 ${isUnread ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>
          {msg.from_name || msg.from_email || "(expéditeur inconnu)"}
        </div>
        <div className={`text-[11px] shrink-0 ${isUnread ? "text-blue-600 font-semibold" : "text-slate-400"}`}>
          {formatRelativeDate(msg.date)}
        </div>
      </div>
      <div className={`text-sm truncate ${isUnread ? "font-semibold text-slate-800" : "font-medium text-slate-600"}`}>
        {msg.subject}
      </div>
      {msg.from_email && msg.from_email !== msg.from_name && (
        <div className="text-[11px] text-slate-400 truncate mt-0.5">{msg.from_email}</div>
      )}
    </button>
  );
}

function MessageViewer({ message, loading, onToggleRead }) {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!message) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-6 text-center">
        <Mail className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">Sélectionnez un message pour le lire</p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto" data-testid="inbox-viewer">
      <div className="border-b border-slate-200 px-6 py-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-xl font-bold text-slate-900 flex-1">{message.subject}</h2>
          <button
            type="button"
            onClick={() => onToggleRead?.(message.uid, !message.is_read)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              message.is_read
                ? "bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200"
                : "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
            }`}
            title={message.is_read ? "Marquer comme non lu" : "Marquer comme lu"}
            data-testid="inbox-toggle-read-btn"
          >
            {message.is_read ? (
              <>
                <MailOpen className="w-3.5 h-3.5" />
                Marquer non lu
              </>
            ) : (
              <>
                <MailCheck className="w-3.5 h-3.5" />
                Marquer lu
              </>
            )}
          </button>
        </div>
        <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-sm">
          <div className="text-slate-400">De</div>
          <div className="text-slate-700"><b>{message.from_name}</b> &lt;{message.from_email}&gt;</div>
          <div className="text-slate-400">À</div>
          <div className="text-slate-700">{message.to || "—"}</div>
          {message.headers?.Cc && (
            <>
              <div className="text-slate-400">Cc</div>
              <div className="text-slate-700">{message.headers.Cc}</div>
            </>
          )}
          <div className="text-slate-400">Date</div>
          <div className="text-slate-700">{formatRelativeDate(message.date)}</div>
        </div>
        {message.attachments?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.attachments.map((a, i) => (
              <div key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-100 text-xs text-slate-700">
                <Paperclip className="w-3 h-3" />
                <span className="font-mono">{a.filename}</span>
                <span className="text-slate-400">({Math.round(a.size / 1024)} KB)</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="px-6 py-5">
        {message.body_html ? (
          <iframe
            srcDoc={message.body_html}
            sandbox=""
            title="Email body"
            className="w-full min-h-[400px] border-0"
            data-testid="inbox-viewer-iframe"
          />
        ) : (
          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed" data-testid="inbox-viewer-text">
            {message.body_text || "(message vide)"}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function InboxPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search, setSearch] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/inbox/status`, { headers: authH() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ configured: true, ok: false, error: e.response?.data?.detail || e.message });
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/inbox/messages?limit=100`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement IMAP");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessage = useCallback(async (uid) => {
    setLoadingDetail(true);
    setSelectedMsg(null);
    try {
      const r = await axios.get(`${API}/admin/inbox/messages/${uid}`, { headers: authH() });
      setSelectedMsg(r.data);
      // Batch #14.177 — Marquage auto-lu côté serveur (auto_mark_read=true par défaut).
      // On reflète localement pour éviter un re-fetch immédiat de toute la liste.
      setItems((prev) => prev.map((m) => (m.uid === uid ? { ...m, is_read: true } : m)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement du message");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const toggleRead = useCallback(async (uid, makeRead) => {
    const path = makeRead ? "read" : "unread";
    // Update optimiste
    setItems((prev) => prev.map((m) => (m.uid === uid ? { ...m, is_read: makeRead } : m)));
    setSelectedMsg((prev) => (prev && prev.uid === uid ? { ...prev, is_read: makeRead } : prev));
    try {
      await axios.post(`${API}/admin/inbox/messages/${uid}/${path}`, {}, { headers: authH() });
    } catch (e) {
      // Rollback en cas d'échec
      setItems((prev) => prev.map((m) => (m.uid === uid ? { ...m, is_read: !makeRead } : m)));
      setSelectedMsg((prev) => (prev && prev.uid === uid ? { ...prev, is_read: !makeRead } : prev));
      toast.error(e.response?.data?.detail || "Erreur lors du changement de statut");
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadMessages();
  }, [loadStatus, loadMessages]);

  const handleSelect = (uid) => {
    setSelectedUid(uid);
    loadMessage(uid);
  };

  const filtered = search.trim()
    ? items.filter((m) => {
        const q = search.toLowerCase();
        return (
          (m.subject || "").toLowerCase().includes(q)
          || (m.from_name || "").toLowerCase().includes(q)
          || (m.from_email || "").toLowerCase().includes(q)
        );
      })
    : items;

  const unreadCount = items.filter((m) => !m.is_read).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-inbox-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => navigate("/admin/documents")}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition"
          aria-label="Retour"
          data-testid="inbox-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <Inbox className="w-5 h-5 text-blue-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-slate-900">Inbox — réception</h1>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-bold" data-testid="inbox-unread-count">
                {unreadCount}
              </span>
            )}
          </div>
          {status?.alias && (
            <div className="text-[11px] text-slate-500 font-mono">{status.alias}</div>
          )}
        </div>
        {status?.retention_days && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full border border-slate-200" title="Rétention : seuls les emails reçus dans les N derniers jours sont visibles">
            <Mail className="w-3 h-3" />
            {status.retention_days} j
          </span>
        )}
        {status && (
          status.ok ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              Connecté
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-full" title={status.error || ""}>
              <AlertCircle className="w-3 h-3" />
              Erreur IMAP
            </span>
          )
        )}
        <button
          onClick={() => { loadMessages(); loadStatus(); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 transition disabled:opacity-50"
          disabled={loading}
          data-testid="inbox-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Rafraîchir
        </button>
      </header>

      {status && !status.ok && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-sm text-rose-700">
          <strong>Connexion IMAP échouée :</strong> {status.error}
        </div>
      )}

      {/* Body : 2 colonnes liste + viewer */}
      <div className="flex h-[calc(100vh-65px)]">
        {/* Liste */}
        <aside className="w-[400px] flex flex-col border-r border-slate-200 bg-white">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher dans la liste…"
                className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:bg-white focus:border-blue-300"
                data-testid="inbox-search"
              />
            </div>
            <div className="text-[11px] text-slate-400 mt-2 px-1">
              {filtered.length} message{filtered.length > 1 ? "s" : ""}
              {items.length > filtered.length && ` (filtré sur ${items.length})`}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="inbox-list">
            {loading && items.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                {search ? "Aucun résultat" : "Boîte vide"}
              </div>
            ) : (
              filtered.map((m) => (
                <MessageListItem
                  key={m.uid}
                  msg={m}
                  isSelected={selectedUid === m.uid}
                  onClick={() => handleSelect(m.uid)}
                />
              ))
            )}
          </div>
        </aside>

        {/* Viewer */}
        <main className="flex-1 flex flex-col bg-white overflow-hidden">
          <MessageViewer message={selectedMsg} loading={loadingDetail} onToggleRead={toggleRead} />
        </main>
      </div>
    </div>
  );
}
